// src/features/notifications/notificationSlice.js
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { notificationAPI } from '../../services/api';

const initialState = {
  notifications: [],
  unreadCount: 0,
  unreadByCategory: {
    All: 0,
    Updates: 0,
    Services: 0,
    Drivers: 0,
    Others: 0
  },
  categories: [],
  pagination: {
    currentPage: 1,
    pageSize: 20,
    totalPages: 1,
    totalCount: 0,
    hasNext: false,
    hasPrevious: false
  },
  filterInfo: null,
  selectedCategory: 'ALL',
  isLoading: false,
  isLoadingMore: false,
  error: null,
  latestNotification: null,
  showNotificationToast: false
};

// Fetch notifications with optional category filter
export const fetchNotifications = createAsyncThunk(
  'notifications/fetchNotifications',
  async ({ category = 'ALL', page = 1 } = {}, { rejectWithValue }) => {
    try {
      const response = await notificationAPI.getNotifications({ category, page });
      return { ...response.data, isLoadMore: page > 1 };
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.Error ||
        error.response?.data?.message ||
        'Failed to fetch notifications'
      );
    }
  }
);

// Mark single notification as read
export const markNotificationAsRead = createAsyncThunk(
  'notifications/markAsRead',
  async (notificationId, { rejectWithValue }) => {
    try {
      const response = await notificationAPI.markAsRead({ notification_id: notificationId });
      return { notificationId, ...response.data };
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.Error ||
        error.response?.data?.message ||
        'Failed to mark notification as read'
      );
    }
  }
);

// Mark all notifications as read
export const markAllAsRead = createAsyncThunk(
  'notifications/markAllAsRead',
  async (category = null, { rejectWithValue }) => {
    try {
      const data = category ? { category } : {};
      const response = await notificationAPI.markAllAsRead(data);
      return { category, ...response.data };
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.Error ||
        error.response?.data?.message ||
        'Failed to mark all as read'
      );
    }
  }
);

// Get only unread count
export const fetchUnreadCount = createAsyncThunk(
  'notifications/fetchUnreadCount',
  async (_, { rejectWithValue }) => {
    try {
      const response = await notificationAPI.getNotifications({ page: 1 });
      return {
        unreadCount: response.data.UnreadCount,
        unreadByCategory: response.data.UnreadByCategory
      };
    } catch (error) {
      return rejectWithValue('Failed to fetch unread count');
    }
  }
);

const notificationSlice = createSlice({
  name: 'notifications',
  initialState,
  reducers: {
    setSelectedCategory: (state, action) => {
      state.selectedCategory = action.payload;
    },
    
    addNewNotification: (state, action) => {
      const notification = action.payload;
      
      state.notifications.unshift({
        Id: notification.id || Date.now(),
        Title: notification.title,
        Body: notification.body,
        Type: notification.data?.type || 'general',
        Category: notification.data?.category || 'Others',
        CategoryKey: notification.data?.category_key || 'OTHERS',
        Data: notification.data || {},
        IsRead: false,
        CreatedAt: notification.timestamp || new Date().toISOString(),
        TimeAgo: 'Just now'
      });
      
      state.unreadCount += 1;
      state.unreadByCategory.All += 1;
      state.latestNotification = notification;
      state.showNotificationToast = true;
    },
    
    hideNotificationToast: (state) => {
      state.showNotificationToast = false;
    },
    
    clearLatestNotification: (state) => {
      state.latestNotification = null;
      state.showNotificationToast = false;
    },
    
    clearNotifications: (state) => {
      state.notifications = [];
      state.unreadCount = 0;
      state.unreadByCategory = {
        All: 0,
        Updates: 0,
        Services: 0,
        Drivers: 0,
        Others: 0
      };
      state.latestNotification = null;
      state.showNotificationToast = false;
    },
    
    clearError: (state) => {
      state.error = null;
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchNotifications.pending, (state, action) => {
        const isLoadMore = action.meta.arg?.page > 1;
        if (isLoadMore) {
          state.isLoadingMore = true;
        } else {
          state.isLoading = true;
        }
        state.error = null;
      })
      .addCase(fetchNotifications.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isLoadingMore = false;
        
        const { Data, Pagination, UnreadCount, UnreadByCategory, Categories, FilterInfo, isLoadMore } = action.payload;
        
        if (isLoadMore) {
          state.notifications = [...state.notifications, ...(Data || [])];
        } else {
          state.notifications = Data || [];
        }
        
        state.unreadCount = UnreadCount || 0;
        state.unreadByCategory = UnreadByCategory || state.unreadByCategory;
        state.categories = Categories || [];
        state.filterInfo = FilterInfo || null;
        
        if (Pagination) {
          state.pagination = {
            currentPage: Pagination.CurrentPage || 1,
            pageSize: Pagination.PageSize || 20,
            totalPages: Pagination.TotalPages || 1,
            totalCount: Pagination.TotalCount || 0,
            hasNext: Pagination.HasNext || false,
            hasPrevious: Pagination.HasPrevious || false
          };
        }
      })
      .addCase(fetchNotifications.rejected, (state, action) => {
        state.isLoading = false;
        state.isLoadingMore = false;
        state.error = action.payload;
      });
    
    builder
      .addCase(markNotificationAsRead.fulfilled, (state, action) => {
        const { notificationId, UnreadCount, UnreadByCategory } = action.payload;
        
        const notification = state.notifications.find(n => n.Id === notificationId);
        if (notification && !notification.IsRead) {
          notification.IsRead = true;
        }
        
        if (UnreadCount !== undefined) {
          state.unreadCount = UnreadCount;
        }
        if (UnreadByCategory) {
          state.unreadByCategory = UnreadByCategory;
        }
      });
    
    builder
      .addCase(markAllAsRead.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(markAllAsRead.fulfilled, (state, action) => {
        state.isLoading = false;
        const { category, UnreadCount, UnreadByCategory } = action.payload;
        
        state.notifications.forEach(notification => {
          if (!category || notification.CategoryKey === category) {
            notification.IsRead = true;
          }
        });
        
        if (UnreadCount !== undefined) {
          state.unreadCount = UnreadCount;
        }
        if (UnreadByCategory) {
          state.unreadByCategory = UnreadByCategory;
        }
      })
      .addCase(markAllAsRead.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      });
    
    builder
      .addCase(fetchUnreadCount.fulfilled, (state, action) => {
        state.unreadCount = action.payload.unreadCount || 0;
        if (action.payload.unreadByCategory) {
          state.unreadByCategory = action.payload.unreadByCategory;
        }
      });
  }
});

export const {
  setSelectedCategory,
  addNewNotification,
  hideNotificationToast,
  clearLatestNotification,
  clearNotifications,
  clearError
} = notificationSlice.actions;

export default notificationSlice.reducer;